import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ShortTextIcon from '@mui/icons-material/ShortText';
import ArticleIcon from '@mui/icons-material/Article';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import TranslateIcon from '@mui/icons-material/Translate';
import SchoolIcon from '@mui/icons-material/School';
import StyleIcon from '@mui/icons-material/Style';
import { EXERCISE_TYPES, CURRICULUM, DEFAULT_STORY_LESSONS } from './typingTutorCurriculum';
import { fetchTypingProgress, fetchCourseStories } from './typingTutorApi';
import { requireStudentAuth } from './typingTutorUtils';
import './typingTutorTheme.css';

const ICON_MAP = {
  keyboard: KeyboardIcon,
  text_fields: TextFieldsIcon,
  short_text: ShortTextIcon,
  article: ArticleIcon,
  menu_book: MenuBookIcon,
};

const isFlashcardStory = (story) => {
  const t = String(story?.contentType || '').toLowerCase();
  return t === 'flashcards' || t === 'flashcard';
};

const difficultyLabel = (charCount) => {
  if (charCount >= 2000) return { label: 'Long', color: '#B45309' };
  if (charCount >= 800) return { label: 'Medium', color: '#6D28D9' };
  return { label: 'Short', color: '#16A34A' };
};

function StoryRow({ story, done, onClick }) {
  const diff = difficultyLabel(story.charCount || story.text?.length || 0);
  const flashcard = isFlashcardStory(story);
  return (
    <div className="tt-lesson-row" onClick={onClick}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography className="tt-sans" fontWeight={600} noWrap>
          {story.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
          <Typography variant="caption" className="tt-sans" sx={{ color: 'var(--tt-muted)' }}>
            {story.charCount || story.text?.length || 0} characters
          </Typography>
          <Chip label={diff.label} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: `${diff.color}18`, color: diff.color }} />
          {flashcard && (
            <Chip
              icon={<StyleIcon sx={{ fontSize: '14px !important' }} />}
              label="Flash cards"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#0EA5E918', color: '#0369A1' }}
            />
          )}
          {story.isTelugu && (
            <Chip icon={<TranslateIcon sx={{ fontSize: '14px !important' }} />} label="Telugu" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
        </Box>
      </Box>
      {done && <CheckCircleIcon sx={{ color: 'var(--tt-correct)', flexShrink: 0 }} />}
    </div>
  );
}

export default function TypingTutorExerciseList() {
  const navigate = useNavigate();
  const { exerciseType } = useParams();
  const exercise = EXERCISE_TYPES.find((e) => e.id === exerciseType);
  const staticLessons = CURRICULUM[exerciseType] || [];
  const isStories = exerciseType === 'stories';

  const [completedSet, setCompletedSet] = useState(new Set());
  const [storiesLoading, setStoriesLoading] = useState(isStories);
  const [storiesError, setStoriesError] = useState('');
  const [courseGroups, setCourseGroups] = useState([]);
  const [defaultStories, setDefaultStories] = useState(DEFAULT_STORY_LESSONS);
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');

  useEffect(() => {
    if (!requireStudentAuth(navigate)) return undefined;
    fetchTypingProgress()
      .then((p) => {
        const set = new Set(
          (p?.completedLessons || p?.completedLessonKeys || []).map(
            (l) => `${l.exerciseType}:${l.lessonId}`,
          ),
        );
        setCompletedSet(set);
      })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!isStories || !requireStudentAuth(navigate)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setStoriesLoading(true);
        const data = await fetchCourseStories();
        if (cancelled) return;
        setCourseGroups(data.courses || []);
        setDefaultStories(
          (data.defaultStories && data.defaultStories.length > 0)
            ? data.defaultStories
            : DEFAULT_STORY_LESSONS,
        );
        setStoriesError('');
      } catch (err) {
        if (!cancelled) {
          setStoriesError(err.response?.data?.message || 'Could not load course stories.');
          setDefaultStories(DEFAULT_STORY_LESSONS);
        }
      } finally {
        if (!cancelled) setStoriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isStories, navigate]);

  const filteredCourses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (courseGroups || [])
      .filter((course) => courseFilter === 'all' || course.courseCode === courseFilter)
      .map((course) => ({
        ...course,
        chapters: (course.chapters || [])
          .map((chapter) => ({
            ...chapter,
            stories: (chapter.stories || []).filter((story) => {
              if (!q) return true;
              const hay = `${story.title} ${chapter.chapterTitle} ${course.courseName}`.toLowerCase();
              return hay.includes(q);
            }),
          }))
          .filter((chapter) => chapter.stories.length > 0),
      }))
      .filter((course) => course.chapters.length > 0);
  }, [courseGroups, search, courseFilter]);

  const filteredDefaultStories = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = defaultStories.length ? defaultStories : DEFAULT_STORY_LESSONS;
    if (!q) return list;
    return list.filter((s) => s.title.toLowerCase().includes(q));
  }, [defaultStories, search]);

  const hasCourseStories = filteredCourses.length > 0;

  if (!exercise) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Exercise type not found.</Alert>
        <Button onClick={() => navigate('/student/typing-tutor')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Container>
    );
  }

  const Icon = ICON_MAP[exercise.icon] || KeyboardIcon;

  const renderStaticList = () => (
    <Box className="tt-card">
      {staticLessons.map((lesson, index) => {
        const done = completedSet.has(`${exerciseType}:${lesson.id}`);
        return (
          <div
            key={lesson.id}
            className="tt-lesson-row"
            onClick={() => navigate(`/student/typing-tutor/${exerciseType}/${lesson.id}`)}
          >
            <Box>
              <Typography className="tt-sans" fontWeight={600}>
                {index + 1}. {lesson.title}
              </Typography>
              <Typography variant="caption" className="tt-sans" sx={{ color: 'var(--tt-muted)', display: 'block' }}>
                {lesson.instructionText || `${lesson.text.length} characters`}
              </Typography>
            </Box>
            {done && <CheckCircleIcon sx={{ color: 'var(--tt-correct)' }} />}
          </div>
        );
      })}
    </Box>
  );

  const renderStoriesList = () => {
    if (storiesLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="success" />
        </Box>
      );
    }

    return (
      <>
        {storiesError && (
          <Alert severity="warning" className="tt-sans" sx={{ mb: 2 }}>
            {storiesError}
          </Alert>
        )}

        <Box className="tt-card" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" className="tt-sans" sx={{ color: 'var(--tt-muted)', mb: 1.5 }}>
            Start with the classic practice stories, then type notes and flash cards from your enrolled courses.
            Only courses you are enrolled in appear here — content updates when chapters are published.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search chapter or lesson title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />
          {courseGroups.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All courses"
                clickable
                color={courseFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setCourseFilter('all')}
                size="small"
              />
              {courseGroups.map((course) => (
                <Chip
                  key={course.courseCode}
                  label={`${course.courseName} (${course.storyCount})`}
                  clickable
                  color={courseFilter === course.courseCode ? 'primary' : 'default'}
                  onClick={() => setCourseFilter(course.courseCode)}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Box>

        {filteredDefaultStories.length > 0 && (
          <Box className="tt-card" sx={{ mb: 3 }}>
            {filteredDefaultStories.map((story, index) => {
              const full = DEFAULT_STORY_LESSONS.find((s) => s.id === story.id) || story;
              const done = completedSet.has(`stories:${story.id}`);
              return (
                <div
                  key={story.id}
                  className="tt-lesson-row"
                  onClick={() => navigate(`/student/typing-tutor/stories/${story.id}`)}
                >
                  <Box>
                    <Typography className="tt-sans" fontWeight={600}>
                      {index + 1}. {full.title}
                    </Typography>
                    <Typography variant="caption" className="tt-sans" sx={{ color: 'var(--tt-muted)', display: 'block' }}>
                      {full.text?.length || full.charCount || 0} characters
                    </Typography>
                  </Box>
                  {done && <CheckCircleIcon sx={{ color: 'var(--tt-correct)' }} />}
                </div>
              );
            })}
          </Box>
        )}

        {hasCourseStories && (
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            Your Course Practice
          </Typography>
        )}

        {!hasCourseStories && filteredDefaultStories.length > 0 && (
          <Alert severity="info" className="tt-sans" sx={{ mb: 2 }}>
            No course practice yet. Enroll in a course with Text, Reader Notes, or Flash Cards to unlock chapter-wise lessons below the default stories.
          </Alert>
        )}

        {!hasCourseStories && filteredDefaultStories.length === 0 && (
          <Alert severity="info" className="tt-sans" sx={{ mb: 2 }}>
            No stories match your search.
          </Alert>
        )}

        {filteredCourses.map((course) => (
          <Box key={course.courseCode} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SchoolIcon sx={{ color: 'var(--tt-accent)' }} />
              <Typography variant="h6" fontWeight={700}>
                {course.courseName}
              </Typography>
              <Chip label={`${course.storyCount} lessons`} size="small" />
            </Box>

            {course.chapters.map((chapter) => (
              <Box key={chapter.chapterId} className="tt-card" sx={{ mb: 2 }}>
                <Typography className="tt-sans" fontWeight={700} sx={{ px: 2, pt: 2, pb: 0.5 }}>
                  {chapter.chapterTitle}
                </Typography>
                <Typography variant="caption" className="tt-sans" sx={{ px: 2, pb: 1, display: 'block', color: 'var(--tt-muted)' }}>
                  {chapter.subjectName} · {chapter.stories.length} exercise{chapter.stories.length !== 1 ? 's' : ''}
                </Typography>
                {chapter.stories.map((story) => (
                  <StoryRow
                    key={story.id}
                    story={story}
                    done={completedSet.has(`stories:${story.id}`)}
                    onClick={() => navigate(`/student/typing-tutor/stories/${story.id}`)}
                  />
                ))}
              </Box>
            ))}
          </Box>
        ))}

      </>
    );
  };

  return (
    <Box className="tt-root">
      <Box className="tt-header">
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate('/student/typing-tutor')} sx={{ color: '#fff' }}>
              <ArrowBackIcon />
            </IconButton>
            <Icon sx={{ color: '#fff' }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>{exercise.title}</Typography>
              <Typography variant="body2" className="tt-sans" sx={{ opacity: 0.9 }}>
                {exercise.description}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {isStories ? renderStoriesList() : renderStaticList()}
      </Container>
    </Box>
  );
}
